import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  BooleanField,
  DateField,
  Edit,
  SimpleForm,
  TextInput,
  BooleanInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  Create,
  Show,
  SimpleShowLayout,
  ArrayField,
  SingleFieldList,
  ChipField,
  FunctionField,
  useRecordContext,
  TopToolbar,
  CreateButton,
  ExportButton,
  ReferenceArrayInput,
  SelectArrayInput,
  FilterButton,
  SearchInput,
  useNotify,
  useRedirect,
  FileInput,
  FileField
} from 'react-admin';
import { Chip, Box, Card, CardHeader, CardContent, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Alert, Typography } from '@mui/material';

// Filters for Data Models
const dataModelFilters = [
  <SearchInput source="q" alwaysOn />,
  <SelectInput
    source="scope"
    label="Scope"
    choices={[
      { id: 'solution-space', name: 'Core (Solution Space)' },
      { id: 'application', name: 'Application' },
      { id: 'platform', name: 'Platform' },
      { id: 'domain', name: 'Domain' },
    ]}
    alwaysOn
  />,
  <SelectInput
    source="environment"
    label="Environment"
    choices={[
      { id: 'all', name: 'All Environments' },
      { id: 'development', name: 'Development' },
      { id: 'staging', name: 'Staging' },
      { id: 'production', name: 'Production' },
    ]}
  />,
  <BooleanInput source="configuration.isActive" label="Active Only" />,
  <BooleanInput source="configuration.isPublished" label="Published Only" />,
];

// DataModel List Component with Grouping and Filters
export const DataModelList = () => (
  <List
    filters={dataModelFilters}
    sort={{ field: 'scope', order: 'ASC' }}
    actions={
      <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ExportButton />
      </TopToolbar>
    }
  >
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <FunctionField
        label="Type"
        render={record => {
          const getScopeConfig = (scope: string) => {
            switch (scope) {
              case 'solution-space':
                return { label: 'Core', color: 'success' };
              case 'application':
                return { label: 'Application', color: 'primary' };
              case 'platform':
                return { label: 'Platform', color: 'info' };
              case 'domain':
                return { label: 'Domain', color: 'warning' };
              default:
                return { label: scope, color: 'default' };
            }
          };
          const config = getScopeConfig(record.scope);
          return (
            <Chip 
              label={config.label} 
              color={config.color as any}
              size="small"
            />
          );
        }}
      />
      <TextField source="name" label="Name" />
      <TextField source="key" label="Key" />
      <FunctionField
        label="Schemas"
        render={record => (
          <Chip 
            label={`${record.schemas?.length || 0} schemas`} 
            size="small"
            variant="outlined"
          />
        )}
      />
      <FunctionField
        label="Domains"
        render={record => (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {record.configuration?.domains?.slice(0, 3).map((domain: any, idx: number) => (
              <Chip 
                key={idx}
                label={domain.name} 
                size="small"
                color="secondary"
                variant="outlined"
              />
            ))}
            {record.configuration?.domains?.length > 3 && (
              <Chip 
                label={`+${record.configuration.domains.length - 3}`} 
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        )}
      />
      <BooleanField source="configuration.isActive" label="Active" />
      <TextField source="currentVersion" label="Version" />
      <DateField source="updatedAt" label="Updated" showTime />
    </Datagrid>
  </List>
);

// DataModel Show Component
export const DataModelShow = () => (
  <Show>
    <SimpleShowLayout>
      <TextField source="name" label="Name" />
      <TextField source="key" label="Key" />
      <TextField source="description" label="Description" />
      <TextField source="scope" label="Scope" />
      <TextField source="environment" label="Environment" />
      <TextField source="currentVersion" label="Current Version" />
      
      <BooleanField source="configuration.isActive" label="Active" />
      <BooleanField source="configuration.isPublished" label="Published" />
      
      <ArrayField source="schemas" label="Schemas">
        <Datagrid>
          <TextField source="name" label="Schema Name" />
          <TextField source="key" label="Schema Key" />
          <TextField source="domain" label="Domain" />
          <TextField source="category" label="Category" />
          <BooleanField source="configuration.isCore" label="Core" />
        </Datagrid>
      </ArrayField>
      
      <ArrayField source="schemas" label="Included Schemas">
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ArrayField>
      
      <ArrayField source="usedByApplications" label="Used By Applications">
        <SingleFieldList>
          <ChipField source="$" />
        </SingleFieldList>
      </ArrayField>
      
      <FunctionField
        label="Compiled Master Schema"
        render={record => (
          <details style={{ marginTop: '10px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
              View Compiled Master Schema
            </summary>
            <pre style={{
              backgroundColor: '#f5f5f5',
              padding: '10px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '400px',
              fontSize: '12px',
              marginTop: '10px'
            }}>
              {record.compiledMasterSchema ? 
                JSON.stringify(JSON.parse(record.compiledMasterSchema), null, 2) : 
                'No compiled schema available'
              }
            </pre>
          </details>
        )}
      />
      
      <DateField source="createdAt" label="Created" />
      <DateField source="updatedAt" label="Updated" />
    </SimpleShowLayout>
  </Show>
);

// DataModel Create Component - Enhanced with Import
interface DataModelCreateFormProps {
  creationMethod: string;
  setCreationMethod: (method: string) => void;
  importedData: any;
  setImportedData: (data: any) => void;
}

const DataModelCreateForm: React.FC<DataModelCreateFormProps> = ({
  creationMethod,
  setCreationMethod,
  importedData,
  setImportedData
}) => {
  const [fileProcessed, setFileProcessed] = useState(false);
  const notify = useNotify();

  // Reset when creation method changes
  useEffect(() => {
    setFileProcessed(false);
    setImportedData(null);
  }, [creationMethod, setImportedData]);

  // Handle file upload for import
  const handleFileUpload = (file: File, fieldName: string) => {
    if (fieldName === 'importFile' && file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importData = JSON.parse(e.target?.result as string);
          
          // Validate that this is a data model export
          if (!importData.key || !importData.schemas) {
            notify('Invalid data model export file. Missing required fields (key, schemas).', { type: 'error' });
            return;
          }

          setImportedData(importData);
          setFileProcessed(true);
          
          // Notify user
          const schemaCount = importData.schemas?.length || 0;
          const hasMetadata = !!importData.exportMetadata;
          
          notify(
            `Data model imported successfully. Includes ${schemaCount} schema(s)${hasMetadata ? ' with metadata' : ''}.`, 
            { type: 'success' }
          );
        } catch (error) {
          console.error('Error parsing import file:', error);
          notify('Error parsing import file. Please check the file format.', { type: 'error' });
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <SimpleForm>
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardHeader title="Create Data Model" />
          <CardContent>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <FormLabel component="legend">Creation Method</FormLabel>
              <RadioGroup
                value={creationMethod}
                onChange={(e) => setCreationMethod(e.target.value)}
                row
              >
                <FormControlLabel 
                  value="manual" 
                  control={<Radio />} 
                  label="Manual Creation" 
                />
                <FormControlLabel 
                  value="import" 
                  control={<Radio />} 
                  label="Import Data Model" 
                />
              </RadioGroup>
            </FormControl>
          </CardContent>
        </Card>
      </Box>

      {/* Basic Data Model Info */}
      <Card sx={{ mb: 2 }}>
        <CardHeader title="Data Model Details" />
        <CardContent>
          <TextInput 
            label="Name" 
            source="name" 
            fullWidth 
            sx={{ mb: 2 }} 
            disabled={creationMethod === 'import'}
            required={creationMethod !== 'import'}
          />
          <TextInput 
            label="Key" 
            source="key" 
            fullWidth 
            sx={{ mb: 2 }}
            disabled={creationMethod === 'import'}
            required={creationMethod !== 'import'}
          />
          <TextInput 
            label="Description" 
            source="description" 
            fullWidth 
            multiline
            rows={3}
            disabled={creationMethod === 'import'}
          />
          {importedData && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Data model imported successfully. Fields above are pre-filled from the import file.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Import includes: {importedData.schemas?.length || 0} schema(s), 
                {importedData.relationships?.length || 0} relationship(s)
              </Typography>
              {importedData.exportMetadata && (
                <Box sx={{ mt: 1, p: 1, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                  <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                    ✅ Export Metadata Included
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#1565c0' }}>
                    Exported: {new Date(importedData.exportMetadata.exportedAt).toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Manual Creation */}
      {creationMethod === 'manual' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader 
            title="Manual Creation Settings" 
            subheader="Configure data model settings manually"
          />
          <CardContent>
            <SelectInput
              source="scope"
              label="Scope"
              choices={[
                { id: 'solution-space', name: 'Solution Space (Core)' },
                { id: 'application', name: 'Application' },
                { id: 'domain', name: 'Domain' },
                { id: 'industry', name: 'Industry Extension' },
              ]}
              defaultValue="application"
              fullWidth
              sx={{ mb: 2 }}
            />
            
            <SelectInput
              source="environment"
              label="Environment"
              choices={[
                { id: 'all', name: 'All Environments' },
                { id: 'development', name: 'Development' },
                { id: 'staging', name: 'Staging' },
                { id: 'production', name: 'Production' },
              ]}
              defaultValue="development"
              fullWidth
              sx={{ mb: 2 }}
            />
            
            <TextInput 
              source="configuration.version" 
              label="Version" 
              defaultValue="1.0.0" 
              fullWidth 
              sx={{ mb: 2 }}
            />
            
            <BooleanInput 
              source="configuration.isActive" 
              label="Active" 
              defaultValue={true} 
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              After creation, you can add schemas and relationships in the edit view.
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Data Model Import */}
      {creationMethod === 'import' && (
        <Card sx={{ mb: 2 }}>
          <CardHeader 
            title="Import Data Model" 
            subheader="Import a complete data model from an exported JSON file"
          />
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Import a complete data model definition including all schemas, relationships, and configuration.
              Use exported files from other environments or AI-generated models.
            </Alert>
            <FileInput 
              source="importFile" 
              label="Data Model Export File" 
              accept=".json"
              onChange={(file) => {
                if (file && !fileProcessed) {
                  handleFileUpload(file, 'importFile');
                }
              }}
            >
              <FileField source="title" title="title" />
            </FileInput>
            {importedData && (
              <TextInput
                source="_enableSave"
                label="Enable Save"
                helperText="Type anything here to enable the save button (e.g., 'ready')"
                placeholder="Type 'ready' to enable save..."
                sx={{ mt: 2 }}
              />
            )}
          </CardContent>
        </Card>
      )}
    </SimpleForm>
  );
};

export const DataModelCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  
  // Lift state to parent so transform can access it
  const [creationMethod, setCreationMethod] = useState('manual');
  const [importedData, setImportedData] = useState(null);

  const onSuccess = () => {
    notify('Data model created successfully');
    redirect('list', 'datamodel-dp');
  };

  const transform = (data: any) => {
    console.log('🔍 [Data Model Create] Transform called with data:', data);
    console.log('🔍 [Data Model Create] Current state - creationMethod:', creationMethod);
    
    if (creationMethod === 'import' && importedData) {
      // Encode as base64 for backend processing
      const importJson = JSON.stringify(importedData);
      const base64Encoded = btoa(unescape(encodeURIComponent(importJson))); // Handle UTF-8
      
      console.log('🔍 [Data Model Create] Encoding imported data');
      console.log('  - Original JSON length:', importJson.length);
      console.log('  - Base64 length:', base64Encoded.length);
      console.log('  - Schemas count:', importedData.schemas?.length || 0);
      
      // IMPORTANT: Don't send schemas array directly - it contains full objects, not ObjectIds
      // The backend middleware will process importedConfiguration and create schemas
      return {
        name: importedData.name,
        key: importedData.key,
        description: importedData.description,
        scope: importedData.scope || 'application',
        environment: importedData.environment || 'all',
        // DON'T include schemas here - let backend create them from importedConfiguration
        // schemas: [], // Backend will populate this
        configuration: {
          version: importedData.version || '1.0.0',
          isActive: true,
          isPublished: false,
          // Don't include domains/relationships yet - backend will create from importedConfiguration
        },
        // Properly encoded for backend parsing
        importedConfiguration: base64Encoded,
        // Remove the FileInput field to prevent React-Admin from processing it
        importFile: undefined
      };
    } else if (creationMethod === 'manual') {
      // Manual creation: Initialize with basic structure
      console.log('🔍 [Data Model Create] Manual creation - initializing');
      return {
        ...data,
        schemas: data.schemas || [],
        configuration: {
          ...data.configuration,
          version: data.configuration?.version || '1.0.0',
          isActive: data.configuration?.isActive !== false,
          isPublished: false,
          domains: [],
          relationships: []
        }
      };
    }
    
    return data;
  };

  return (
    <Create mutationOptions={{ onSuccess }} transform={transform}>
      <DataModelCreateForm 
        creationMethod={creationMethod}
        setCreationMethod={setCreationMethod}
        importedData={importedData}
        setImportedData={setImportedData}
      />
    </Create>
  );
};

// DataModel Edit Component - Enhanced with nested schema management
export { default as DataModelEdit } from './DataModelEdit';

